import '../styles/FormEditor.scss';
import '../styles/PublicShare.scss';

import { type SendAFormModalProps } from '../../sendaform/SendAFormModal';
import { type PublicShareWizardProps } from '../../publicshare/PublicShareWizard';

import React, { ComponentType, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { AppPage } from '../../components/AppPage/AppPage';
import { Button, Modal } from 'semantic-ui-react';
import loadable from '@loadable/component';
import CalculationRunner from '../../calculation/CalculationRunner';
import {
  initFormEditor as initFormEditorReduxAction,
  formEditorUpdateName as formEditorUpdateNameReduxAction,
  formEditorUpdateTags as formEditorUpdateTagsReduxAction,
  saveForm as saveFormReduxAction,
  submitSharedForm as submitSharedFormReduxAction,
  updateFieldValues as updateFieldValuesReduxAction,
  updateFormulaValues as updateFormulaValuesReduxAction,
  updateValidationResults as updateValidationResultsReduxAction,
  updateTemplateVersion as updateTemplateVersionReduxAction,
  completeForm as completeFormReduxAction,
  formEditorCloseOutdated as formEditorCloseOutdatedReduxAction,
  reopenForm as reopenFormReduxAction,
  duplicateForm as duplicateFormReduxAction,
  transferForm as transferFormReduxAction,
  deleteForm as deleteFormReduxAction,
  formEditorCloseError as formEditorCloseErrorReduxAction,
  setFormEditorView as setFormEditorViewReduxAction,
  savePublicForm as savePublicFormReduxAction,
  updateFieldHighlight as updateFieldHighlightReduxAction
} from './../actions';
import { StatusComplete, FormStatusDraft } from '../../icons/icons';
import Loader from '../../components/loader';
import { FieldProperties } from '../../calculation/Constants';
import GeneratePdfModal from './GeneratePdfModal';
import ChangeTemplateVersionModal from './ChangeTemplateVersionModal';
import { fetchGroups as fetchGroupsReduxAction } from '../../usersgroups/groups/actions';
import { fetchUsers as fetchUsersReduxAction } from '../../usersgroups/users/actions';
import TemplateListModal from '../../templates/components/TemplateListModal';
import { showConfirm, showBanner } from '../../helpers/MessageUtility';
import PostPublicShare from '../../publicshare/PostPublicShare';
import InvalidPublicShare from '../../publicshare/InvalidPublicShare';
import { FieldTypes, TemplateActionTypes } from '../../helpers/constants';
import { apimCall } from '../../helpers/api';
import ErrorBoundary from '../../components/ErrorBoundary';
import Header from './Header';
import { registerInactivityTimeoutEvent, unregisterInactivityTimeoutEvent } from '../../helpers/InactivityTimeoutHelper';
import { SaveFormForRecovery } from '../recovery';
import { handleUrlParameterValues } from '../urlParameterValues';
import FormHistoryModal from '../../forms/components/FormHistoryModal';
import WebViewModal from './CustomActions/WebViewModal';
import Error from '../../error/Error';
import { RootState } from 'gf-types';
import { replaceInvalidCharacters, validateUrl } from '../../helpers/helpers';
import { GoFormzServices } from '../../AppSettings';
import { FormPreviewType, ValidationResultsUpdate, ValueChangedCallbackFunc, ValueChangedPayload } from '../formeditor-types';
import { FieldValueUpdate, FormEditorView, FormulaValueUpdate } from '../formeditor-types';
import { tryWhen } from '../../helpers/utility';
import { DataLayoutPosition, DataLayoutTab, FieldPosition, Template, TemplateColumn, TemplateField, TemplatePage } from '../../types/template-types';
import { FormTag, FormVersion, PublicForm } from '../../types/form-types';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { useActions } from '../../hooks/useActions';
import { useNavigate, useParams } from 'react-router-dom';
import { convertFieldFormatToMoment } from '../../helpers/DateTimeUtility';
import { getDateTimeValue } from '../helpers';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import TagsModal from '../../tags/components/TagsModal';
import TransferModal from '../../components/TransferModal';
import FormStatusChangeModal from './FormStatusChangeModal';
import GfModal from '../../gfcomponents/GfModal/GfModal';
import FormVersionRevertModal from '../../components/FormVersionRevertModal/FormVersionRevertModal';
import RouterPrompt from '../../components/RouterPrompt';
import { useApim } from '../../hooks/useApi';
import GfLoader from '../../gfcomponents/GfLoader/GfLoader';
import { canIUse, Features } from '../../helpers/PermissionUtility';
import { ViewType } from '../../publicshare/constants';

export interface FormEditorProps {
  sessionId?: string;
  sharedAccessToken?: string;
  publicFormData?: PublicForm;
}

const FormView = loadable(() => import(/* webpackChunkName: "formView" */ './FormView/FormView'), { fallback: <ViewLoader /> });
const ListView = loadable(() => import(/* webpackChunkName: "listView" */ './ListView/ListView'), { fallback: <ViewLoader /> });
const SendAFormModal = lazy<ComponentType<SendAFormModalProps>>(() => import('../../sendaform/SendAFormModal'));
const PublicShareWizard = lazy<ComponentType<PublicShareWizardProps>>(() => import('../../publicshare/PublicShareWizard'));

export default function FormEditor(props: FormEditorProps) {
  const updateFieldValuesAction: (list: FieldValueUpdate[]) => void = useActions(updateFieldValuesReduxAction);
  const updateFormulaValuesAction: (list: FormulaValueUpdate[]) => void = useActions(updateFormulaValuesReduxAction);
  const updateValidationResultsAction: (list: ValidationResultsUpdate[]) => void = useActions(updateValidationResultsReduxAction);
  const setFormEditorViewAction: (view: FormEditorView) => void = useActions(setFormEditorViewReduxAction);
  const updateFieldHighlightAction: (fields: FieldValueUpdate[]) => void = useActions(updateFieldHighlightReduxAction);

  const [
    formEditorUpdateNameAction,
    formEditorUpdateTagsAction,
    initFormEditorAction,
    saveFormAction,
    submitSharedFormAction,
    updateTemplateVersionAction,
    completeFormAction,
    duplicateFormAction,
    formEditorCloseOutdatedAction,
    reopenFormAction,
    fetchGroupsAction,
    formEditorCloseErrorAction,
    fetchUsersAction,
    transferFormAction,
    deleteFormAction,
    savePublicFormAction
  ] = useActions([
    formEditorUpdateNameReduxAction,
    formEditorUpdateTagsReduxAction,
    initFormEditorReduxAction,
    saveFormReduxAction,
    submitSharedFormReduxAction,
    updateTemplateVersionReduxAction,
    completeFormReduxAction,
    duplicateFormReduxAction,
    formEditorCloseOutdatedReduxAction,
    reopenFormReduxAction,
    fetchGroupsReduxAction,
    formEditorCloseErrorReduxAction,
    fetchUsersReduxAction,
    transferFormReduxAction,
    deleteFormReduxAction,
    savePublicFormReduxAction
  ]);

  const reduxProps = useAppSelector((state: RootState) => {
    return {
      formId: state.formeditor.form?.id,
      templateVersionId: state.formeditor.form?.templateVersionId,
      formName: state.formeditor.form?.name,
      completedDate: state.formeditor.form?.completedDate,
      pages: state.formeditor.template?.pages,
      dataLayouts: state.formeditor.template?.dataLayouts,
      template: state.formeditor.template,
      sasToken: state.shared.sasToken,
      isSaving: state.formeditor.isSaving,
      completeInProgress: state.formeditor.completeInProgress,
      userName: state.profile.user.name,
      userId: state.profile.user.id,
      user: state.profile.user,
      isAdmin: state.profile.isAdmin,
      form: state.formeditor.form,
      formulaValues: state.formeditor.formulaValues,
      userMap: state.formeditor.userMap,
      isUnsaved: state.formeditor.isUnsaved,
      isNewForm: state.formeditor.isNewForm,
      error: state.formeditor.error,
      itemCollections: state.formeditor.itemCollections,
      defaultFormName: state.formeditor.template?.defaultFormName,
      printConfigs: state.formeditor.template?.printConfigs,
      postTransfer: state.formeditor.transferred,
      isFetching: state.formeditor.isFetching,
      publicShareSubmitted: state.formeditor.publicShareSubmitted,
      pdfPath: state.formeditor.pdfPath,
      publicShareInvalid: state.formeditor.error?.message,
      outdated: state.formeditor.outdated,
      previewMode: state.formeditor.previewMode,
      policyInfo: state.formeditor.policyInfo,
      versionId: state.formeditor.versionId,
      notFound: state.formeditor.notFound,
      view: state.formeditor.view,
      savedPublicForm: state.formeditor.savedPublicForm,
      invalidFieldValues: state.formeditor.invalidFieldValues
    };
  });

  const [initialCalculationsHaveRun, setInitialCalculationsHaveRun] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [takeOwnershipOpen, setTakeOwnershipOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [changeTemplateVersionModal, setChangeTemplateVersionModal] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [formHistoryModal, setFormHistoryModal] = useState<{ name: string; id: string }>(null);
  const [formRevertModal, setFormRevertModal] = useState<{ formId: string; versionId: string }>(null);
  const [templateListOpen, setTemplateListOpen] = useState(false);
  const [publicFormsOpen, setPublicFormsOpen] = useState(false);
  const [customActionOpen, setCustomActionOpen] = useState<TypeMePlease>(null);
  const [previewView, setPreviewView] = useState<FormPreviewType>('desktop');
  const [versions, versionsLoading] = useApim<FormVersion[]>(
    GoFormzServices.TemplateService,
    `/forms/${reduxProps.formId}/versions`,
    reduxProps?.formId == null || props.sharedAccessToken != null || !reduxProps.versionId
  );
  const calculationResultBatch = useRef<TypeMePlease[]>([]);
  const handleCalcBatchTimeout = useRef<NodeJS.Timeout>(null);
  const calcRunner = useRef<CalculationRunner>(null);
  const bringToDataLayoutPosition = useRef<(tab: DataLayoutTab, position: DataLayoutPosition) => void>(null);
  const bringToFormViewPosition = useRef<(page: TemplatePage, position: FieldPosition) => void>(null);

  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isPublicForm = !!props.sharedAccessToken;

  const allowFormRevert =
    reduxProps.versionId == null || versionsLoading || (versions?.length ?? 0) <= 1 ? false : versions[versions.length - 1]?.versionId !== reduxProps.versionId;

  useEffect(() => {
    initFormEditorAction(props.sharedAccessToken, id, props.sessionId, props.publicFormData);
  }, [props.sharedAccessToken, id, props.sessionId, props.publicFormData]);

  useEffect(() => {
    const onInactivityTimeoutWarning = () => {
      if (reduxProps.isUnsaved) {
        console.log('saving form state to localstorage');
        SaveFormForRecovery(reduxProps.form);
      }
    };
    registerInactivityTimeoutEvent(onInactivityTimeoutWarning);
    return () => unregisterInactivityTimeoutEvent(onInactivityTimeoutWarning);
  }, [reduxProps.isUnsaved, reduxProps.form]);

  const prevTemplate = useRef<Template>(null);
  useEffect(() => {
    if (reduxProps.template) {
      if (!prevTemplate.current) {
        let view: FormEditorView = 'form';
        if ((reduxProps.pages?.length ?? 0) === 0) {
          view = 'list';
        } else if ((reduxProps.dataLayouts?.length ?? 0) === 0) {
          view = 'form';
        } else if (reduxProps.policyInfo?.viewType === 1) {
          view = 'list';
        } else if (!isPublicForm && localStorage.getItem('form_default_view') === 'list' && reduxProps.dataLayouts.length !== 0) {
          view = 'list';
        }
        setFormEditorViewAction(view);

        setInitialCalculationsHaveRun(false);
        calcRunner.current = new CalculationRunner(reduxProps.formId, reduxProps.template, getItemCollection, isPublicForm);
        calcRunner.current.init().then(() => {
          // onLoad run all calcs for new forms to set default values and only onLoadCalcs for existing forms (positional calcs)
          const formValues = reduxProps.form.values;
          if (!reduxProps.completedDate) {
            calcRunner.current
              .process({ runOnLoadCalcsOnly: !reduxProps.isNewForm, formValues: formValues || null })
              .then((calcResult) => handleCalculationResult(calcResult))
              .then(() =>
                handleUrlParameterValues(reduxProps.template, reduxProps.form, reduxProps.userId, reduxProps.isAdmin, valueChanged, reduxProps.itemCollections)
              )
              .then(() => {
                setInitialCalculationsHaveRun(true);
              });
          } else {
            calcRunner.current
              .process({ runOnLoadCalcsOnly: true, formValues: formValues || null })
              .then((calcResult) => handleCalculationResult(calcResult))
              .then(() => {
                setInitialCalculationsHaveRun(true);
              });
          }
        });
      } else if (reduxProps.template.versionId !== prevTemplate.current.versionId) {
        //Reinitialize calc runner if template version changes
        calcRunner.current = new CalculationRunner(reduxProps.formId, reduxProps.template, getItemCollection, isPublicForm);
        calcRunner.current.init();
      }
    }
    prevTemplate.current = reduxProps.template;
  }, [reduxProps.template]);

  const getItemCollection = (id: string): TypeMePlease => {
    const itemCollection = reduxProps.itemCollections.find((x) => x.id === id);
    if (itemCollection) {
      return itemCollection;
    }
    return null;
  };

  const handleCalculationResult = (calcResult) => {
    const formulaValueUpdates: FormulaValueUpdate[] = [];
    const fieldValuesUpdates: FieldValueUpdate[] = [];
    const validationFormulaUpdates: ValidationResultsUpdate[] = [];

    for (const calculation of calcResult.calculations) {
      if (calculation.Target.IsFormName) {
        if (calculation.Result !== reduxProps.formName) {
          const result = calculation.Result ? replaceInvalidCharacters(calculation.Result) : null;
          formEditorUpdateNameAction(result, false, true);
        }
      } else if (calculation.Target.AutoTagId) {
        let tags = [...reduxProps.form.tags];
        if (!tags) tags = [];

        const oldTagIndex = tags.findIndex((x) => x.autoTagIds.includes(calculation.Target.AutoTagId));
        const newTagIndex = tags.findIndex((x) => x.name === calculation.Result);

        // If the autotag isn't changing, just leave as is unless it's adding a tag
        if (oldTagIndex === newTagIndex && newTagIndex !== -1) {
          continue;
        }

        // Remove id from old tag
        if (oldTagIndex >= 0) {
          const newAutoTagIds = [...tags[oldTagIndex].autoTagIds];
          const indexOfId = newAutoTagIds.indexOf(calculation.Target.AutoTagId);
          newAutoTagIds.splice(indexOfId, 1);
          tags[oldTagIndex] = { ...tags[oldTagIndex], autoTagIds: newAutoTagIds, operation: newAutoTagIds.length === 0 ? 3 : 1, isAutoTag: true };
        }

        // If new tag is already on the form, add this id to it
        if (newTagIndex >= 0) {
          const newAutoTagIds = tags[newTagIndex]?.autoTagIds;

          // Skip if autotag id is already on tag or if the tag isn't from autotags
          if (!newAutoTagIds.includes(calculation.Target.AutoTagId) && (newAutoTagIds.length > 0 || tags[newTagIndex]?.operation)) {
            newAutoTagIds.push(calculation.Target.AutoTagId);
            tags[newTagIndex] = { ...tags[newTagIndex], autoTagIds: newAutoTagIds, operation: 1, isAutoTag: true };
          }
        }
        // Otherwise add a new tag if calculation result has a value
        else if (calculation.Result) {
          const newTag = { name: calculation.Result, operation: 1, autoTagIds: [calculation.Target.AutoTagId], isAutoTag: true } as FormTag;
          tags = [...tags, newTag];
        }

        formEditorUpdateTagsAction(tags);
      } else if (calculation.PropertyName) {
        if (calculation.PropertyName === 'FieldValidation') {
          validationFormulaUpdates.push({
            fieldId: calculation.Target.Id,
            fieldName: calculation.Target.Name,
            result: calculation.Result,
            errorSettings: calculation.Target.Properties.getItem(FieldProperties.FieldValidationErrorSettings)
          });
        } else {
          formulaValueUpdates.push({ id: calculation.Target.Id, key: calculation.PropertyName, value: calculation.Result });
        }
      } else {
        const fieldType = calculation.Target.Properties.getItem(FieldProperties.FieldType);
        const dateTimeFormat = calculation.Target.Properties.getItem(FieldProperties.DateTimeFormat);

        const fieldValuesUpdate: FieldValueUpdate = {
          value: calculation.Result,
          fieldId: calculation.Target.Id,
          rowNumber: calculation.Target.Row,
          columnNumber: calculation.Target.Column,
          fieldType,
          dateTimeFormat: calculation.Target.Properties.getItem(FieldProperties.DateTimeFormat)
        };

        if (fieldType === FieldTypes.Date || fieldType === FieldTypes.DateTime) {
          const dateValue = getDateTimeValue(fieldType, calculation, dateTimeFormat);
          fieldValuesUpdate.dateTimeValue = dateValue?.toISOString();
          fieldValuesUpdate.value = dateValue?.format(convertFieldFormatToMoment(dateTimeFormat));
        } else if (fieldType === FieldTypes.Time) {
          const timeValue = getDateTimeValue(fieldType, calculation, dateTimeFormat);
          fieldValuesUpdate.timeValue = timeValue?.toISOString();
          fieldValuesUpdate.value = timeValue?.format(convertFieldFormatToMoment(dateTimeFormat));
        }

        fieldValuesUpdates.push(fieldValuesUpdate);
      }
    }

    updateFormulaValuesAction(formulaValueUpdates);
    updateFieldValuesAction(fieldValuesUpdates);
    if (canIUse(Features.FieldValidations)) {
      updateValidationResultsAction(validationFormulaUpdates);
      updateFieldHighlightAction(fieldValuesUpdates);
    }
  };

  const onFormulaResult = (calcResult, callback) => {
    if (callback) {
      callback(calcResult.Result);
    }

    //clear the old setTimeout function, if it exists
    clearTimeout(handleCalcBatchTimeout.current);

    //add this result to our next batch
    calculationResultBatch.current.push(calcResult);

    //handle our batch and then clear the array, but only if we don't call onFormulaResult again within the next 20 milliseconds
    handleCalcBatchTimeout.current = setTimeout(() => {
      handleCalculationResult({ calculations: calculationResultBatch.current });
      calculationResultBatch.current = [];
    }, 20);
  };

  const valueChanged = (value: ValueChangedPayload, field: TemplateField, rowNumber?: number, column?: TemplateColumn, callback?: ValueChangedCallbackFunc) => {
    const values: FieldValueUpdate[] = [
      {
        value: value?.value,
        fieldId: field.id,
        fieldName: field.name,
        rowNumber,
        columnNumber: column ? column.columnNumber : null,
        fieldType: field.type
      }
    ];
    updateFieldValuesAction(values);
    const formValues = reduxProps.form.values;
    const calcName = `${field.name}${column ? ` ${column.sortOrder + 1} ${rowNumber + 1}` : ''}`;
    const newValue = value?.value ? { value: value.value, dateValue: value.dateTimeValue } : null;

    calcRunner.current.process({
      target: field,
      formValues: formValues || null,
      newValue,
      calcName,
      onFormulaResult: (r) => onFormulaResult(r, callback)
    });
  };

  const openPdfModal = async () => {
    if (reduxProps.isUnsaved) {
      await saveFormAction();
    }
    setPdfOpen(!pdfOpen);
  };

  const closePdfModal = () => {
    setPdfOpen(!pdfOpen);
  };

  const openTagsModal = async () => {
    if (reduxProps.isUnsaved) {
      await saveFormAction();
    }
    setTagsOpen(!tagsOpen);
  };

  const closeTagsModal = () => {
    setTagsOpen(!tagsOpen);
  };

  const openFormHistoryModal = (formName, formId) => {
    setFormHistoryModal({
      name: formName,
      id: formId
    });
    analyticsTrack('Form History Clicked', {});
  };

  const closeFormHistoryModal = () => {
    setFormHistoryModal(null);
  };

  const onRevertToVersion = (formId: string, versionId: string) => {
    setFormHistoryModal(null);
    setFormRevertModal({ formId, versionId });
  };

  const closeRevertModal = () => {
    setFormRevertModal(null);
  };

  const openChangeTemplateVersionModal = async () => {
    setChangeTemplateVersionModal(!changeTemplateVersionModal);
  };

  const onTemplateVersionChange = async (templateVersionId) => {
    updateTemplateVersionAction(templateVersionId);
    closeChangeTemplateVersionModal();
  };

  const closeChangeTemplateVersionModal = () => {
    setChangeTemplateVersionModal(!changeTemplateVersionModal);
  };

  const openTemplateListModal = () => {
    if (reduxProps.isUnsaved) {
      showConfirm(
        {
          title: 'Confirm',
          msg: 'There are unsaved changes. Do you want to save them?'
        },
        async (result) => {
          if (result) {
            await saveFormAction();
          }
          setTemplateListOpen(true);
        }
      );
    } else {
      setTemplateListOpen(true);
    }
  };

  const closeTemplateListModal = () => {
    setTemplateListOpen(false);
  };

  const newForm = (id: string) => {
    navigate(`/form/new?templateId=${id}`);
  };

  const toggleTransferModal = () => {
    if (!transferOpen) {
      fetchGroupsAction(null, true);
      fetchUsersAction();
    }
    setTransferOpen(!transferOpen);
  };

  const confirmSubmitSharedForm = () => {
    submitSharedFormAction();
  };

  const savePublicForm = () => {
    savePublicFormAction();
  };

  const deleteForm = () => {
    showConfirm(
      {
        title: 'Confirm Delete',
        msg: `Are you sure you want to delete ${reduxProps.formName}?`
      },
      async (result) => {
        if (result) {
          await deleteFormAction(reduxProps.formId);
          navigate('/forms');
          showBanner('Form Deleted');
        }
      }
    );
  };

  const completeForm = () => {
    if (reduxProps.versionId) {
      return;
    }

    if (reduxProps.previewMode) {
      completeFormAction(reduxProps.view, reduxProps.previewMode);
      analyticsTrack('Preview - Test Complete', {});
    } else {
      completeFormAction(reduxProps.view);
    }
  };

  const performOutdatedAction = (action) => {
    if (action === 'Save') {
      saveFormAction('Form saved', true);
    } else if (action === 'Complete') {
      completeFormAction(reduxProps.view, false, true);
    }

    formEditorCloseOutdatedAction();
  };

  const transferForm = (selectedItem, transferType) => {
    return saveFormAction().then(() => transferFormAction(selectedItem, transferType, true, navigate));
  };

  const openPublicFormsModal = () => {
    if (canIUse(Features.PublicFormV2)) {
      analyticsTrack('PF Flow - Forms Editor');
    }

    setPublicFormsOpen(true);
  };

  const closePublicFormsModal = () => {
    setPublicFormsOpen(false);
  };

  const openTakeOwnershipModal = () => {
    setTakeOwnershipOpen(true);
  };

  const closeTakeOwnershipModal = () => {
    setTakeOwnershipOpen(false);
  };

  const updatePreviewView = (view: FormPreviewType) => {
    setPreviewView(view);
    analyticsTrack('Preview - View Selected', {
      View: view
    });
  };

  const setView = (view: FormEditorView) => {
    if (reduxProps.view === view) {
      return;
    }

    localStorage.setItem('form_default_view', view);
    bringToDataLayoutPosition.current = null;
    bringToFormViewPosition.current = null;
    setFormEditorViewAction(view);
  };

  const takeOwnership = () => {
    const userId = reduxProps.user.id;
    const USER_TYPE = 0;
    transferFormAction(userId, USER_TYPE, false, navigate);
    setTakeOwnershipOpen(false);
  };

  const clickCustomAction = async (action) => {
    if (reduxProps.isUnsaved && !reduxProps.previewMode) {
      await saveFormAction();
    }
    switch (action.actionType) {
      case TemplateActionTypes.WorkflowTrigger:
        try {
          await apimCall(GoFormzServices.TemplateService, `/forms/${reduxProps.formId}/actions/${action.id}/execute`, 'POST');
          showBanner(`${action.name} workflow triggered.`);
        } catch (err) {
          showBanner(`Error executing ${action.name}. Please try again.`, 'error');
        }
        break;

      case TemplateActionTypes.WebView:
        setCustomActionOpen(action);
        break;
      case TemplateActionTypes.DeepLink:
        if (!validateUrl(action.webUrl)) {
          showBanner(`Invalid URL for ${action.name}. Please try again.`, 'error');
          break;
        }
        window.open(await calcRunner.current.executeFormula(action.webUrl, reduxProps.form.values ? reduxProps.form.values : null), '_blank');
        break;
      default:
        console.warn('action type not implemented');
        break;
    }
  };

  const bringFieldPositionIntoView = (page: TemplatePage, position: FieldPosition) => {
    setView('form');
    tryWhen(
      () => bringToFormViewPosition.current !== null,
      () => {
        bringToFormViewPosition.current(page, position);
      }
    );
  };

  const bringDataLayoutPositionIntoView = (tab: DataLayoutTab, position: DataLayoutPosition) => {
    setView('list');
    tryWhen(
      () => bringToDataLayoutPosition.current !== null,
      () => {
        bringToDataLayoutPosition.current(tab, position);
      }
    );
  };

  const registerBringFieldPositionIntoView = (bringToPosition: (page: TemplatePage, position: FieldPosition) => void) => {
    bringToFormViewPosition.current = bringToPosition;
  };

  const registerBringToDataLayoutPosition = (bringToPosition: (tab: DataLayoutTab, position: DataLayoutPosition) => void) => {
    bringToDataLayoutPosition.current = bringToPosition;
  };

  const getBody = () => {
    let body = null;
    if (reduxProps.publicShareSubmitted) {
      return <PostPublicShare pdfPath={reduxProps.pdfPath} />;
    }

    if (!reduxProps.isFetching && reduxProps.notFound) {
      return <Error errorCode={404} />;
    }

    const isOwned = reduxProps.form?.userId === reduxProps.user.id;
    if (reduxProps.publicShareInvalid && !reduxProps.form && props.sharedAccessToken) {
      return <InvalidPublicShare errorMessage={reduxProps.publicShareInvalid} />;
    }

    const isDraft = !reduxProps.completedDate;

    if (reduxProps.form) {
      if (reduxProps.view === 'form') {
        body = (
          <FormView
            pages={reduxProps.pages}
            sasToken={reduxProps.sasToken}
            valueChanged={valueChanged}
            renderFields={initialCalculationsHaveRun}
            formulaValues={reduxProps.formulaValues}
            registerBringFieldPositionIntoView={registerBringFieldPositionIntoView}
            previewView={previewView}
          />
        );
      } else if (reduxProps.view === 'list' && initialCalculationsHaveRun) {
        body = <ListView valueChanged={valueChanged} registerBringToDataLayoutPosition={registerBringToDataLayoutPosition} previewView={previewView} />;
      }
    }

    return (
      <div className="form-editor">
        <Header
          formName={reduxProps.formName}
          formId={reduxProps.formId}
          form={reduxProps.form}
          userMap={reduxProps.userMap}
          isUnsaved={reduxProps.isUnsaved}
          isNewForm={reduxProps.isNewForm}
          user={reduxProps.user}
          userName={reduxProps.userName}
          template={reduxProps.template}
          completedDate={reduxProps.completedDate}
          completeInProgress={reduxProps.completeInProgress}
          isFetching={reduxProps.isFetching}
          isSaving={reduxProps.isSaving}
          sharedAccessToken={props.sharedAccessToken}
          publicShareInvalid={reduxProps.publicShareInvalid}
          openTemplateListModal={openTemplateListModal}
          clickCustomAction={clickCustomAction}
          reopenForm={reopenFormAction}
          duplicateForm={duplicateFormAction}
          openFormHistoryModal={openFormHistoryModal}
          openChangeTemplateVersionModal={openChangeTemplateVersionModal}
          deleteForm={deleteForm}
          completeForm={completeForm}
          saveForm={() => saveFormAction()}
          allowFormRevert={allowFormRevert}
          onRevertToVersion={onRevertToVersion}
          formEditorUpdateName={formEditorUpdateNameAction}
          initialCalculationsHaveRun={initialCalculationsHaveRun}
          confirmSubmitSharedForm={confirmSubmitSharedForm}
          openTakeOwnershipModal={openTakeOwnershipModal}
          getStatusIcon={getStatusIcon}
          view={reduxProps.view}
          setView={setView}
          previewMode={reduxProps.previewMode}
          versionId={reduxProps.versionId}
          disableTransferButton={reduxProps.form === undefined || reduxProps.form === null || !isDraft || !isOwned}
          openTransferModal={toggleTransferModal}
          openTagsModal={openTagsModal}
          isFormOpen={reduxProps.form !== undefined && reduxProps.form !== null}
          openPdfModal={openPdfModal}
          openPublicFormsModal={openPublicFormsModal}
          formStatus={isDraft ? 'draft' : 'complete'}
          pages={reduxProps.pages}
          dataLayouts={reduxProps.dataLayouts}
          bringToFieldPosition={bringFieldPositionIntoView}
          bringToDataLayoutPosition={bringDataLayoutPositionIntoView}
          previewView={previewView}
          setPreviewView={updatePreviewView}
          savePublicForm={savePublicForm}
        />
        <div className={`main-panel-wrapper no-sidebar ${reduxProps.previewMode ? 'form-preview' : ''} ${previewView}`}>
          <Loader
            isLoading={
              !!(
                reduxProps.isFetching ||
                reduxProps.isSaving ||
                reduxProps.completeInProgress ||
                (!initialCalculationsHaveRun && (reduxProps.template || reduxProps.formId))
              )
            }
          />
          <ErrorBoundary>{body}</ErrorBoundary>
        </div>
        <FormStatusChangeModal />
        {transferOpen ? (
          <TransferModal
            currentOwnerId={reduxProps.form.userId === null ? reduxProps.form.groupId : reduxProps.form.userId}
            toggleTransferModal={toggleTransferModal}
            transferForms={transferForm}
            isTransferring={reduxProps.isSaving || reduxProps.isFetching}
          />
        ) : null}
        {pdfOpen ? (
          <GeneratePdfModal
            formId={reduxProps.formId}
            versionId={reduxProps.versionId}
            templateId={reduxProps.template.id}
            onClose={closePdfModal}
            printConfigs={reduxProps.printConfigs ? reduxProps.printConfigs : []}
            saveFirst={reduxProps.isUnsaved || reduxProps.isNewForm}
            saveForm={saveFormAction}
            template={reduxProps.template ? reduxProps.template : null}
            form={reduxProps.form ? reduxProps.form : null}
          />
        ) : null}
        {tagsOpen ? <TagsModal formId={reduxProps.formId} formName={reduxProps.formName} onClose={closeTagsModal} /> : null}
        {templateListOpen ? <TemplateListModal onClose={closeTemplateListModal} selectTemplate={newForm} /> : null}
        {formHistoryModal ? (
          <FormHistoryModal open name={formHistoryModal.name} id={formHistoryModal.id} onClose={closeFormHistoryModal} onRevertToVersion={onRevertToVersion} />
        ) : null}
        {formRevertModal && <FormVersionRevertModal formId={formRevertModal.formId} versionId={formRevertModal.versionId} onClose={closeRevertModal} />}
        {changeTemplateVersionModal ? (
          <ChangeTemplateVersionModal
            templateId={reduxProps.template.id}
            templateVersionId={reduxProps.templateVersionId}
            templateName={reduxProps.template.name}
            isUnsaved={reduxProps.isUnsaved}
            onSave={onTemplateVersionChange}
            onClose={closeChangeTemplateVersionModal}
          />
        ) : null}
        {customActionOpen && customActionOpen.actionType === TemplateActionTypes.WebView ? (
          <WebViewModal
            action={customActionOpen}
            onClose={() => setCustomActionOpen(null)}
            calculationRunner={calcRunner.current}
            formValues={reduxProps.form.values ? reduxProps.form.values : null}
            template={reduxProps.template}
            valueChanged={valueChanged}
          />
        ) : null}
        {publicFormsOpen ? (
          <Suspense fallback={<GfLoader />}>
            {canIUse(Features.PublicFormV2) ? (
              <SendAFormModal
                sendType="form"
                launchedFrom="formeditor"
                initialState={{
                  formId: reduxProps.form.id,
                  name: reduxProps.form.name,
                  viewType: reduxProps.view === 'form' ? ViewType.FormView : ViewType.ListView
                }}
                onCompleted={closePublicFormsModal}
              />
            ) : (
              <PublicShareWizard
                close={() => closePublicFormsModal()}
                onSave={() => {
                  /* noop */
                }}
                formId={reduxProps.form.id}
                formName={reduxProps.form.name}
                skipFormTemplateSelect
              />
            )}
          </Suspense>
        ) : null}
        <GfModal
          title={reduxProps.error?.title}
          className="formstatuschange-modal"
          open={!!reduxProps.error}
          primaryText="Ok"
          onPrimaryClick={formEditorCloseErrorAction}
          onClose={formEditorCloseErrorAction}
        >
          <div className="main-text">
            {reduxProps.error?.message}
            <div>
              <br />
              {reduxProps.error?.items?.map((item) => (
                <div key={item}>{item}</div>
              ))}
            </div>
          </div>
        </GfModal>

        <GfModal
          title="Warning: Transferring Away from Current Owner"
          className="formstatuschange-modal"
          open={takeOwnershipOpen}
          primaryText="Continue"
          secondaryText="Cancel"
          onPrimaryClick={takeOwnership}
          onSecondaryClick={closeTakeOwnershipModal}
          onClose={closeTakeOwnershipModal}
          semanticUiModalProps={{ size: null }}
        >
          <div className="main-text">
            Taking ownership of this form will transfer the form away from the current owner. Any unsaved or unsynced changes will be lost upon transfer.
          </div>
        </GfModal>
        {reduxProps.outdated ? (
          <Modal size="tiny" open onClose={formEditorCloseErrorAction} dimmer="inverted">
            <Modal.Header>Warning</Modal.Header>
            <Modal.Content>
              <span style={{ fontWeight: 'bold' }}>{reduxProps.outdated.message}</span>
              <span> Saving this version will overwrite previous changes and may cause a loss of data. </span>
              <a href={window.location.href} target="_blank">
                View Updated Form
              </a>
            </Modal.Content>
            <Modal.Actions>
              <Button
                content={`Continue to ${reduxProps.outdated.action}`}
                className="tertiary"
                style={{ float: 'left', marginLeft: '0.25em', paddingLeft: '0' }}
                onClick={() => performOutdatedAction(reduxProps.outdated.action)}
              />
              <Button content="Cancel" className="primary" onClick={formEditorCloseOutdatedAction} />
            </Modal.Actions>
          </Modal>
        ) : null}
        <RouterPrompt when={(reduxProps.isUnsaved && !reduxProps.previewMode && !reduxProps.versionId) || false} />
      </div>
    );
  };

  return (
    <AppPage
      title={reduxProps.formName}
      meta={{ name: 'viewport', content: 'width=device-width, height=device-height, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no' }}
    >
      {getBody()}
    </AppPage>
  );
}

function ViewLoader() {
  return (
    <div className="ui active inverted dimmer">
      <div className="ui loader" />
    </div>
  );
}

function getStatusIcon(status) {
  switch (status) {
    case 'complete':
      return <StatusComplete />;
    case 'draft':
      return <FormStatusDraft />;
    default:
      return null;
  }
}
